import { useEffect, useState } from "react";
import "./style/main.scss";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Dashboard from "./pages/dashboard/Dashboard";
import Main from "./pages/main/Main";
import LogIn from "./pages/LogIn";
import Config from "./pages/config/Config";
import Report from "./pages/report/Report";
import Redirect from "./pages/Redirect";
import { selectUserData } from "./features/store";
import { useDispatch, useSelector } from "react-redux";
import ManageAccounts from "./pages/manageAccounts/ManageAccounts";
import VerticalNavBar from "./components/verticalNavBar/VerticalNavBar";
import { SidebarData } from "./components/verticalNavBar/navBarData";
import ResetPassword from "./pages/ResetPassword";
import { setUserData } from "./features/slices/userDataSlice";
import User from "./models/User";
import NotFound from "./pages/NotFound";
import {
    addCurrentEvent,
    checkIn,
    deleteCurrentEvent,
    setCurrentEvent,
    setEventReference,
    updateCurrentEvent,
} from "./features/slices/currentEventSlice";
import { getSensorEventLive } from "./apis/SensorEvent.Api";
import { setBuilding } from "./features/slices/buildingSlice";
import { setRoom } from "./features/slices/roomSlice";
import { setSensor, updateSensor } from "./features/slices/sensorSlice";
import { setDevice, updateDevice } from "./features/slices/deviceSlice";
import { addFeedback, setFeedback, setFeedbackReference } from "./features/slices/feedbackSlice";
import { ES } from "./configs/global";
import { useSnackbar } from "notistack";
import { getBuilding } from "./apis/Building.Api";
import { getDevice } from "./apis/Device.Api";
import { getAccountByToken } from "./apis/Auth.Api";
import Fetching from "./pages/Fetching";
import moment from "moment";
import { getFeedbackLog } from "./apis/Feedback.Api";
import { getRoom } from "./apis/Room.Api";
import { getSensor } from "./apis/Sensor.Api";

function App() {
    const WEB_URL = process.env.REACT_APP_WEB_SERVER_URL;
    const { enqueueSnackbar } = useSnackbar();

    const dispatch = useDispatch();
    const userData = useSelector(selectUserData);
    const [fetchedUser, setFetchedUser] = useState(false);
    const [fetchedData, setFetchedData] = useState(false);
    const [displayNavBar, setDisplayNavBar] = useState(true);
    const [selectedBuilding, setSelectedBuilding] = useState(""); // selected building of dashboard & main
    useEffect(() => {
        getUserByLocalStorage();
    }, []);

    // When user data is updated, re-fetch all data
    useEffect(() => {
        if (!userData.token) return; // don't fetch data when log-out
        if (userData.reset) return; // don't fetch data when reset
        initializeData();
    }, [userData]);

    async function getUserByLocalStorage() {
        setFetchedUser(false);

        console.log("INIT | Fetching user data...");
        const token = localStorage.getItem("token");
        if (!token) {
            console.log("INIT | No stored token, navigate to log in.");
            setFetchedUser(true);
            return;
        }
        // stores user data
        setFetchedUser(false);
        const res = await getAccountByToken();
        if (res.data.networkError) {
            enqueueSnackbar(res.data.message, { variant: "error" });
            return;
        }
        if (!res.data.indicator) {
            enqueueSnackbar("Signature has expired, please log in again.", {
                variant: "info",
            });
            console.log("INIT | Token invalid, navigate to log in.");
            localStorage.removeItem("token");
            setFetchedUser(true);
            return;
        }
        let user = new User({
            ...res.data.message,
            token: token,
        });

        dispatch(setUserData(JSON.parse(JSON.stringify(user))));
        setFetchedUser(true);
        console.log("INIT | User data fetched.", user);
    }

    async function initializeData() {
        console.log("INIT | Fetching data...");

        setFetchedData(false);

        await initCurrentEvent();
        await initBuilding();
        await initRoom();
        await initSensor();
        await initDevice();
        await initFeedback();
        await initEventSource();
        setFetchedData(true);
        enqueueSnackbar("Data fetched successfully.", { variant: "success" });
        console.log("INIT | Done fetching data.");
    }

    async function initCurrentEvent() {
        let data = await getSensorEventLive();
        let eventList = data.data.message;
        dispatch(setCurrentEvent(eventList));
        console.log("INIT | Current Event Initialized", eventList);
    }

    async function initBuilding() {
        let buildings = await getBuilding();
        buildings = buildings.data.message;
        dispatch(setBuilding(buildings));

        if (Array.isArray(buildings) && buildings.length > 0) {
            setSelectedBuilding(
                localStorage.getItem("selectedBuilding") ||
                    buildings.reduce((a, b) => (a.building_name.localeCompare(b.building_name) ? b : a)).building_id
            );
        }
        console.log("INIT | Buildings initialized", buildings);
    }
    async function initRoom() {
        let rooms = await getRoom();
        rooms = rooms.data.message;
        dispatch(setRoom(rooms));
        dispatch(setFeedbackReference(rooms));
        dispatch(setEventReference(rooms));
        console.log("INIT | Rooms initialized", rooms);
    }

    async function initSensor() {
        let sensors = await getSensor();
        sensors = sensors.data.message;
        dispatch(setSensor(sensors));
        console.log("INIT | Sensors initialized", sensors);
    }

    async function initDevice() {
        let devices = await getDevice();
        devices = devices.data.message;
        dispatch(setDevice(devices));
        console.log("INIT | Device initialized", devices);
    }

    async function initFeedback() {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        let feedback = await getFeedbackLog({ start_time: moment(today).format("YYMMDDHHmmssSSS") });
        feedback = feedback.data.message;

        dispatch(setFeedback(feedback));
        console.log("INIT | Feedback initialized", feedback);
    }
    async function initEventSource() {
        console.log("INIT | Event source connected");
        const sse = new EventSource(WEB_URL + "/EventSource" + "?userId=" + userData.userId);

        sse.onmessage = (event) => {
            let message = JSON.parse(event.data);
            handleMessageChange(message);
        };
        sse.onerror = (e) => {
            console.log(moment().format("YY/MM/DD HH:mm:ss") + " | Event source error", e);
            enqueueSnackbar("Event Source Error", {
                variant: "error",
                preventDuplicate: true,
            });
            //sse.close();
        };
        return () => {
            sse.close();
        };
    }
    function handleMessageChange(message) {
        if (!message) return;
        const queryString = window.location.search;
        const params = new URLSearchParams(queryString);
        const logParam = params.get("log");

        if (message.operation === "error" && logParam && logParam === "true") {
            console.log(moment().format("YYYY/MM/DD HH:mm:ss") + " | Event Source Message", message);
        }

        if (message.operation === ES.ADD_EVENT) {
            dispatch(addCurrentEvent(message.payload));
            return;
        } else if (message.operation === ES.UPDATE_EVENT) {
            dispatch(updateCurrentEvent(message.payload));
            return;
        } else if (message.operation === ES.UPDATE_SENSOR) {
            dispatch(updateSensor(message.payload));
            return;
        } else if (message.operation === ES.DELETE_EVENT) {
            dispatch(deleteCurrentEvent(message.payload));
            return;
        } else if (message.operation === ES.FEEDBACK) {
            dispatch(addFeedback(message.payload));
            return;
        } else if (message.operation === ES.UPDATE_DEVICE) {
            dispatch(updateDevice(message.payload));
            return;
        } else if (message.operation === ES.CHECK_IN) {
            dispatch(checkIn(message.payload));
            return;
        } else if (message.operation === ES.ERROR) {
            // enqueueSnackbar("An internal error has occurred.", {variant: "error"});
            return;
        }
    }

    return fetchedUser ? (
        <BrowserRouter>
            {!userData.token || userData.reset ? (
                <>
                    <Routes>
                        <Route path="/" element={<Redirect prevPath={"/"} />} />
                        <Route path="/log-in" element={!userData.token ? <LogIn /> : <Redirect />} />
                        <Route path="/reset-password" element={userData.reset ? <ResetPassword /> : <Redirect />} />
                        <Route path="/dashboard" element={<Redirect prevPath={"/dashboard"} />} />
                        <Route path="/main" element={<Redirect prevPath={"/main"} />} />
                        <Route path="/config" element={<Redirect prevPath={"/config"} />} />
                        <Route path="/report" element={<Redirect prevPath={"/report"} />} />
                        <Route path="/manage-accounts" element={<Redirect prevPath={"/manage-accounts"} />} />

                        <Route path="/*" element={<Redirect />} />
                    </Routes>
                </>
            ) : (
                <>
                    {fetchedData ? (
                        <div className="loggedInMain">
                            {displayNavBar && <VerticalNavBar vbData={SidebarData} />}

                            <div className="logged-in-content">
                                <Routes>
                                    <Route path="/" element={<Redirect />} />
                                    <Route path="/log-in" element={<Redirect />} />
                                    <Route path="/reset-password" element={<Redirect />} />
                                    <Route
                                        path="/dashboard"
                                        element={
                                            <Dashboard
                                                selectedBuilding={selectedBuilding}
                                                setSelectedBuilding={setSelectedBuilding}
                                            />
                                        }
                                    />
                                    <Route
                                        path="/main"
                                        element={
                                            <Main
                                                selectedBuilding={selectedBuilding}
                                                setSelectedBuilding={setSelectedBuilding}
                                            />
                                        }
                                    />
                                    <Route path="/config" element={<Config />} />
                                    <Route path="/report" element={<Report />} />
                                    <Route path="/manage-accounts" element={<ManageAccounts />} />

                                    <Route path="/*" element={<NotFound setDisplayNavBar={setDisplayNavBar} />} />
                                </Routes>
                            </div>
                        </div>
                    ) : (
                        <Fetching title={"Loading Data..."} />
                    )}
                </>
            )}
        </BrowserRouter>
    ) : (
        <Fetching title={"Fetching your account..."} />
    );
}

export default App;
